import { PropsWithChildren, useMemo } from "react";
import {
  ThemeProvider,
  useTheme,
  Components,
  Theme,
} from "@mui/material/styles";
import { useInputAppearance } from "hooks/useInputAppearance";
import { InModalContext } from "contexts/InModalContext";
import { INPUT_APPEARANCE_KEY, muiVariant } from "utils/inputAppearance";

/**
 * Nadpisania MUI dot. WARIANTU pól (wypełnione/konturowe/standardowe) — jedyna
 * właściwość wyglądu pól, której NIE da się ograniczyć samym CSS (to prop struktury
 * pola, nie styl). Dzięki `defaultProps.variant` surowe MUI `<TextField>`/`<Select>`
 * w modalu przyjmują wariant z preferencji „Wygląd pól". Wymiary (zaokrąglenie/gęstość/
 * wysokość/odstęp) obsługuje CSS ograniczony do `[data-app-modal]` (styles/global.scss).
 * Puste, gdy użytkownik nie wybrał świadomie preferencji (wtedy bez zmian).
 */
const buildVariantOverrides = (
  variant: "filled" | "outlined" | "standard",
  explicit: boolean,
): Components<Theme> => {
  if (!explicit) return {};
  return {
    MuiTextField: { defaultProps: { variant } },
    MuiFormControl: { defaultProps: { variant } },
  };
};

/**
 * Wspólny „mianownik wyglądowy" pól wewnątrz OKNA MODALNEGO. Zawija dzieci w trójkę:
 *  1. zagnieżdżony `ThemeProvider` z wariantem pola z preferencji — LOKALNY dla modala,
 *     więc nie wycieka na pola poza modalami (rozwiązuje problem „motyw = globalny"),
 *  2. `InModalContext=true` — `components/Input` bierze wariant z preferencji,
 *  3. kontener `[data-app-modal]` — CSS (global.scss) stosuje wymiary do pól MUI oraz
 *     surowych `<input type=text>`/`<textarea>`.
 * Efekt: `components/Input`, surowe MUI i surowe pola tekstowe wyglądają SPÓJNIE i
 * reagują na ustawienie. Kontener ma `display: contents`, więc nie wpływa na układ.
 */
export const ModalFieldScope = ({ children }: PropsWithChildren) => {
  const [view] = useInputAppearance();
  const explicit =
    typeof window !== "undefined" &&
    !!window.localStorage.getItem(INPUT_APPEARANCE_KEY);
  const outerTheme = useTheme();

  const theme = useMemo(
    () =>
      ({
        ...outerTheme,
        components: {
          ...outerTheme.components,
          ...buildVariantOverrides(muiVariant(view.variant), explicit),
        },
      }) as Theme,
    [outerTheme, view.variant, explicit],
  );

  return (
    <ThemeProvider theme={theme}>
      <InModalContext.Provider value={true}>
        <div data-app-modal="" style={{ display: "contents" }}>
          {children}
        </div>
      </InModalContext.Provider>
    </ThemeProvider>
  );
};

export default ModalFieldScope;
